import React from "react";
import { Kbd } from "@src/common/components/Kbd/Kbd";

// Minimal shape the palette list needs. Both row actions
// (TableActionDropdownItem) and bulk actions (BulkShipmentAction) satisfy this.
export interface PaletteActionItem {
  key: string;
  label: React.ReactNode;
  icon?: React.ReactNode;
}

interface PaletteActionListProps<T extends PaletteActionItem> {
  actions: T[];
  highlightedIndex: number;
  setHighlightedIndex: (index: number) => void;
  itemRefs: React.MutableRefObject<(HTMLButtonElement | null)[]>;
  onSelect: (item: T) => void;
  // Optional shortcut letter to render for an item (e.g. row actions).
  getShortcut?: (key: string) => string | undefined;
  // Optional predicate to flag destructive items so they render in red.
  isDestructive?: (item: T) => boolean;
}

export function PaletteActionList<T extends PaletteActionItem>({
  actions,
  highlightedIndex,
  setHighlightedIndex,
  itemRefs,
  onSelect,
  getShortcut,
  isDestructive,
}: PaletteActionListProps<T>) {
  if (actions.length === 0) {
    return (
      <div className="px-4 py-6 text-center text-sm text-gray-400 dark:text-gray-500">
        No matching actions
      </div>
    );
  }

  return (
    <div className="max-h-[320px] overflow-y-auto py-1">
      {actions.map((item, index) => {
        const shortcut = getShortcut?.(item.key);
        const isHighlighted = index === highlightedIndex;
        const destructive = isDestructive?.(item) ?? false;
        return (
          <button
            key={item.key}
            ref={(el) => {
              itemRefs.current[index] = el;
            }}
            type="button"
            onClick={() => onSelect(item)}
            onMouseMove={() => setHighlightedIndex(index)}
            className={`flex w-full items-center justify-between gap-3 px-4 py-2 text-left text-sm ${
              isHighlighted ? "bg-slate-100 dark:bg-gray-700" : "bg-transparent"
            }`}
          >
            <span
              className={`flex items-center gap-2.5 ${
                destructive
                  ? "text-red-600 dark:text-red-400"
                  : "text-gray-700 dark:text-gray-200"
              }`}
            >
              {item.icon}
              <span>{item.label}</span>
            </span>
            {shortcut && <Kbd>{shortcut}</Kbd>}
          </button>
        );
      })}
    </div>
  );
}
